import React, { useEffect, useState } from 'react'
import DigitalVaultSearchView from '../../views/digital-vault-search/digital-vault-search.view'
import { CompanyService, LocalStorageService, SemanticSearchService } from '../../../services'
import { withRouter, useLocation } from "react-router-dom"
import { withTranslation } from "react-i18next"
import { useTranslation } from 'react-i18next'
import { NIL } from 'uuid'

const DigitalVaultSearchAction = (props) => {
    const {
        toggleCompletedRequesting,
        toggleLoadingRequesting,
        toggleErrorRequesting
    } = props;

    const url = process.env.REACT_APP_MIFIRMA_API;
    const localStorageService = new LocalStorageService();
    const companyService = new CompanyService(url)
    const semanticSearchService = new SemanticSearchService(url);
    const token = localStorageService.getLocalStorageItem("token").idToken;
    const { state } = useLocation();

    const [isLoading, setIsLoading] = useState(true)
    const [noDataMessage, setDataMessage] = useState('')

    const [companies, setCompanies] = useState([])
    const [documents, setDocuments] = useState([])

    const [selectedCompanyId, setSelectedCompanyId] = useState(0)
    const [selectedDocument, setSelectedDocument] = useState(null)
    const [selectedCategoryId, setSelectedCategoryId] = useState(0)

    const [filterUsers, setFilterUsers] = useState([])
    const [filterCategories, setFilterCategories] = useState([])
    const [filterCreatedDates, setFilterCreatedDates] = useState([])
    const [filterTypeDocuments, setFilterTypeDocuments] = useState([])

    const { t } = useTranslation()

    const loadFilters = async (comId, catId) => {
        setIsLoading(true);

        try {
            const response = await companyService.getUsersCompanies(token, process.env.REACT_APP_ENDPOINT_COMPANIES_ORIGIN_DIGITAL_VAULT_SEARCH)
            if (response && response.data) {
                if (response.data.length === 0) {
                    setDataMessage(t('digitalVaultSearchView.noData.message'))
                    setIsLoading(false)
                    return
                }

                const normalized = response.data.map(item => ({
                    id: item.company?.id || null,
                    companyName: item.company?.companyName || "",
                    categories: item.company?.categories || [],
                    raw: item
                }))

                const sortedCompanies = normalized.sort((a, b) => {
                    return a.companyName.toLowerCase().localeCompare(b.companyName.toLowerCase())
                })

                const companiesWithPlaceholder = [
                    {
                        id: "",
                        companyName: t('digital.vault.search.document.placeholder.search.company'),
                        categories: []
                    },
                    ...sortedCompanies
                ]

                setCompanies(companiesWithPlaceholder)

                const exists = sortedCompanies.some(c => c.id === comId)

                setSelectedCompanyId(exists ? comId : "")
                setSelectedCategoryId(catId ?? "")
                setDocuments([])
                setIsLoading(false)
            } else {
                throw new Error(response?.message || "Ocurrió un error obteniendo las compañías.")
            }
        } catch (e) {
            setIsLoading(false);
            toggleErrorRequesting(true, e.message, 4000, true)
        }
    }

    const loadDocuments = async (comId, catId, searchText = '', isIntelligentSearch = false, companiesAvailable = companies, globalSearch = false, searchMode = 'any'
    ) => {
        toggleLoadingRequesting(true);

        try {
            if (!comId || comId === 0 || comId === '') {
                setDocuments([])
                toggleLoadingRequesting(false)
                toggleErrorRequesting(true, t('digital.vault.search.document.error.select.company'), 3000, false)
                return
            }
            // Validar que haya texto de búsqueda
            if (!searchText || searchText.trim() === '') {
                setDocuments([]);
                toggleLoadingRequesting(false)
                toggleErrorRequesting(true, t('digital.vault.search.document.error.enter.search.text'), 3000, false)
                return
            }

            const selectedCompany = companiesAvailable.find(c => c.id === comId)

            if (!selectedCompany) {
                toggleLoadingRequesting(false)
                toggleErrorRequesting(true, t('digital.vault.search.document.error.company.not.found'), 3000, false)
                return
            }

            // Switch apagado (false): lexical
            // Switch prendido (true): semantic (o mixed según necesites)
            const searchType = isIntelligentSearch ? 'semantic' : 'lexical'
            let lexicalType

            if (isIntelligentSearch) {
                lexicalType = process.env.REACT_APP_TYPE_LEXICAL_DEFAULT
            } else {
                if (searchMode === 'any') {
                    lexicalType = process.env.REACT_APP_TYPE_LEXICAL_OR
                }

                if (searchMode === 'all') {
                    lexicalType = process.env.REACT_APP_TYPE_LEXICAL_AND
                }

                if (searchMode === 'exact') {
                    lexicalType = process.env.REACT_APP_TYPE_LEXICAL_PHRASE
                }
            }

            const payload = {
                searchText: searchText.trim(),
                searchType: searchType,
                companyId: comId
            }

            if (!isIntelligentSearch) {
                payload.lexicalType = lexicalType
            }

            const semanticResponse = await semanticSearchService.performSemanticSearch(token, payload)

            if (semanticResponse.statusCode === 200 && semanticResponse.data) {
                const { results, searchType: responseSearchType, categories, typeDocuments, users, createdDates } = semanticResponse.data

                // Guardar las opciones de filtrado
                setFilterCategories(categories || [])
                setFilterTypeDocuments(typeDocuments || [])
                setFilterUsers(users || [])
                setFilterCreatedDates(createdDates || [])

                // Validar que el tipo de búsqueda coincida con lo solicitado
                const expectedSearchType = isIntelligentSearch ? 'semantic' : 'lexical'
                if (responseSearchType !== expectedSearchType) { }

                // Obtener los resultados según el tipo de búsqueda realizada
                let searchResults = []

                if (results) {
                    // Combinar resultados lexicales y semánticos
                    const lexicalResults = results.lexical || []
                    const semanticResults = results.semantic || []

                    // Priorizar resultados según el tipo de búsqueda
                    if (isIntelligentSearch) {
                        // Para búsqueda semántica, priorizar resultados semánticos
                        searchResults = [...semanticResults, ...lexicalResults]
                    } else {
                        // Para búsqueda léxica, priorizar resultados léxicos
                        searchResults = [...lexicalResults, ...semanticResults]
                    }
                }

                if (searchResults.length === 0) {
                    setDocuments([])
                    toggleErrorRequesting(true, 'No se encontraron documentos', 3000, false)
                    toggleLoadingRequesting(false)
                    return
                }

                // Mapear los datos del endpoint semántico al formato esperado por la tabla
                const mappedDocuments = searchResults.map(doc => {
                    // El endpoint retorna la estructura con document anidado
                    const documentData = doc.document || {}
                    const fileData = doc.file || {}
                    const userInfo = documentData.user || {}
                    const typeDocumentInfo = documentData.typeDocument || {}
                    const categoryInfo = documentData.category || {}

                    return {
                        // IDs principales - IMPORTANTE: usar el id de document.id
                        documentId: documentData.id || '',
                        id: documentData.id || '',

                        // Información del documento
                        documentName: documentData.name || doc.request_name || '',
                        description: documentData.description || doc.description || '',

                        // Nuevo campo: resumen por IA
                        summary: doc.documentInfo?.summary || '',

                        // Información de búsqueda semántica (específica del endpoint)
                        snippet: doc.snippet ? (doc.snippet.length > 1000 ? doc.snippet.substring(0, 1000) + '...' : doc.snippet) : '',
                        rank_data: doc.rank_data || 0,
                        fts_rank: doc.fts_rank || 0,
                        accuracy_percentage: doc.accuracy_percentage || 0,
                        searchMethod: doc.searchMethod || responseSearchType || '',

                        // Nuevos campos de filtrado
                        isValid: documentData.isValid || false,
                        signingInGovo: fileData.signingInGovo || false,

                        // Información del archivo si existe
                        fileName: fileData.fileName || '',
                        fileSize: fileData.size || '',
                        fileId: fileData.id || '',

                        // Fechas
                        documentStartDate: documentData.startDate
                            ? new Date(`${documentData.startDate}${documentData.startDate?.includes('00:00:00') ? '' : ' 00:00:00'}`)
                            : null,
                        documentValidation: documentData.finishDate
                            ? new Date(`${documentData.finishDate}${documentData.finishDate?.includes('00:00:00') ? '' : ' 00:00:00'}`)
                            : null,

                        // Categorías y clasificación
                        categoryName: categoryInfo.name || '',
                        subcategoryName: documentData.subcategoryName || '',
                        typeDocumentName: typeDocumentInfo.name || '',
                        typeDocumentInfoType: typeDocumentInfo.infoType || '',

                        // Usuario propietario
                        userName: userInfo.fullName || '',
                        userId: userInfo.id || '',

                        // Otros campos relevantes
                        documentAlias: documentData.documentAlias || '',
                        mandatory: documentData.mandatory || false,
                        penalized: documentData.penalized || false,
                        confidential: documentData.confidential || false,

                        // IDs relacionados
                        categoryId: categoryInfo.id || '',
                        subcategoryId: documentData.subcategoryId || '',
                        companyId: doc.company_id || comId,
                        clientId: doc.client_id || ''
                    }
                })

                setDocuments(mappedDocuments)
            } else {
                const errorMsg = t('digital.vault.search.document.error.search.failed')
                toggleLoadingRequesting(false)
                setDocuments([])
                toggleErrorRequesting(true, errorMsg, 4000, false)
                return
            }
            toggleLoadingRequesting(false);
        } catch (e) {
            toggleLoadingRequesting(false)
            setDocuments([])

            let userFriendlyMessage = t('digital.vault.search.document.error.generic')
            if (e.message && e.message.includes('network')) {
                userFriendlyMessage = t('digital.vault.search.document.error.network')
            } else if (e.message && e.message.includes('timeout')) {
                userFriendlyMessage = t('digital.vault.search.document.error.timeout')
            } else if (e.message && e.message.includes('401')) {
                userFriendlyMessage = t('digital.vault.search.document.error.unauthorized')
            }

            toggleErrorRequesting(true, userFriendlyMessage, 4000, false)
        }

        setSelectedCompanyId(comId);
        setSelectedCategoryId(catId);
    }

    useEffect(() => {
        if (state) {
            loadFilters(state.selectedCompanyId, state.selectedCategoryId);
            setSelectedDocument(state.selectedDocument);
        } else {
            loadFilters();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])


    return (
        <DigitalVaultSearchView
            isLoading={isLoading}
            companies={companies}
            selectedCompanyId={selectedCompanyId}
            selectedCategoryId={selectedCategoryId}
            documents={documents}
            loadDocuments={loadDocuments}
            noDataMessage={noDataMessage}
            selectedDocument={selectedDocument}
            setSelectedDocument={setSelectedDocument}
            filterCategories={filterCategories}
            filterTypeDocuments={filterTypeDocuments}
            filterUsers={filterUsers}
            filterCreatedDates={filterCreatedDates}
        />
    )
}

export default withRouter(withTranslation()(DigitalVaultSearchAction));